import { Due, HMS } from "src/models/Time";

export enum TimeFormat {
  "YYYY-MM-DD",
  "YYYY/MM/DD",
  "MM/DD/YY",
  "YY/MM/DD",
  "MM/DD, YYYY",
}

const DEFAULT_LOCALE_DATE_STRING_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
};

/**
 * 배송 예정일의 기준 시간대. 발송은 한국 창고에서 하고, 예정일 값도 한국 날짜로 만든다(draft 는 그 날짜
 * 15:00 KST — `libs/orders/estimated-shipping-time.ts`, 장바구니는 `yyyy-MM-dd` → UTC 자정 = 같은 날 09:00 KST).
 * 표시를 이 시간대로 고정하지 않으면 보는 쪽 시간대가 날짜를 바꾼다 — 미국 서부에선 하루 전 날짜가 되고,
 * 서버(UTC)가 그린 결제 화면과 브라우저의 하이드레이션 날짜가 갈린다.
 */
export const SHIP_DATE_TIME_ZONE = "Asia/Seoul";

export default class TimeUtil {
  /**
   * ⚠️ `locale` 은 가능하면 항상 넘긴다 — `useLocale().locale || languageCode`.
   *
   * 폴백이 `"us"` 였는데, 이건 지원되지 않는 태그라 `Intl` 이 **예외 없이 조용히**
   * 시스템/브라우저 기본 로케일로 떨어진다("영어" 의도가 관철되지 않고, 한국 사용자가
   * 영문 페이지에서 한국어 날짜를 보게 된다 — #5126 과 같은 계열). 사이트 기본 로케일인
   * `"en"` 으로 교체했다. 마찬가지 이유로 번역 디렉토리 키코드(`kr`/`jp`/`cn`)는 넘기면 안 된다.
   */
  static toLocaleDateString = (
    time: number | string | Date,
    locale?: Intl.LocalesArgument | null,
    options: Intl.DateTimeFormatOptions = DEFAULT_LOCALE_DATE_STRING_OPTIONS,
  ): string => {
    return new Date(time).toLocaleDateString(locale ?? "en", options);
  };

  /** 배송 예정일 표기 — 보는 사람의 시간대와 무관하게 한국 날짜로(`SHIP_DATE_TIME_ZONE`). */
  static toShipDateString = (
    time: number | string | Date,
    locale?: Intl.LocalesArgument | null,
  ): string =>
    TimeUtil.toLocaleDateString(time, locale, {
      ...DEFAULT_LOCALE_DATE_STRING_OPTIONS,
      timeZone: SHIP_DATE_TIME_ZONE,
    });

  static secondsToHMS = (totalSeconds: number): HMS => {
    const hour = Math.floor(totalSeconds / 60 / 60);
    const minute = Math.floor((totalSeconds - hour * 60 * 60) / 60);
    const second = Math.floor(totalSeconds - hour * 60 * 60 - minute * 60);
    return {
      hour,
      minute,
      second,
    };
  };

  static dateStringToDue = (dateString: string): Due | undefined => {
    if (!dateString || dateString.length !== 8) {
      return undefined;
    }
    const year = +dateString.substring(0, 4);
    const month = +dateString.substring(4, 6);
    const date = +dateString.substring(6);
    return {
      year,
      month: month - 1,
      date,
    };
  };

  static yyyymmddToDate = (yyyymmdd: number | string) => {
    const _string = `${yyyymmdd}`;
    if (!_string || _string.length !== 8) {
      return new Date();
    }
    const year = _string.substring(0, 4);
    const month = _string.substring(4, 6);
    const date = _string.substring(6);
    return new Date(+year, +month - 1, +date);
  };

  static dateStringToMillis = (dateString: string): number => {
    if (!dateString || dateString.length !== 8) {
      return 0;
    }
    const year = +dateString.substring(0, 4);
    const month = +dateString.substring(4, 6);
    const date = +dateString.substring(6);
    return +new Date(year, month - 1, date);
  };

  static toDue = (dateString: string | number) => {
    const abroad = new Date(dateString);
    abroad.setUTCHours(abroad.getUTCHours() + 9);
    const year = abroad.getUTCFullYear();
    const month = abroad.getUTCMonth();
    const date = abroad.getUTCDate();
    return { year, month, date };
  };
}
